import { useRef } from 'react';
import { Card, CardContent, Input } from '../../../components/ui';
import type { HeroImage, HeroTag } from '../types/microsite.types';

interface HeroSectionProps {
    heroImages?: HeroImage[];
    onHeroImagesChange: (images: HeroImage[]) => void;
    heroTags?: HeroTag[];
    onHeroTagsChange: (tags: HeroTag[]) => void;
}

export default function HeroSection({
    heroImages = [],
    onHeroImagesChange,
    heroTags = [],
    onHeroTagsChange,
}: HeroSectionProps) {
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Hero Image Handlers
    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files || []);

        // Validate files
        const validFiles = files.filter((file) => {
            if (!file.type.startsWith('image/')) {
                alert(`${file.name} is not an image file`);
                return false;
            }
            if (file.size > 5 * 1024 * 1024) {
                alert(`${file.name} is larger than 5MB`);
                return false;
            }
            return true;
        });

        // Convert files to HeroImage format
        const newImages: HeroImage[] = validFiles.map((file) => ({
            file,
            altText: '',
        }));

        onHeroImagesChange([...heroImages, ...newImages]);

        // Reset input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleRemoveImage = (index: number) => {
        onHeroImagesChange(heroImages.filter((_, i) => i !== index));
    };

    const handleUpdateAltText = (index: number, altText: string) => {
        const updated = [...heroImages];
        updated[index] = { ...updated[index], altText };
        onHeroImagesChange(updated);
    };

    const handleClick = () => {
        fileInputRef.current?.click();
    };

    // Hero Tag Handlers
    const handleAddTag = () => {
        onHeroTagsChange([...heroTags, { label: '', description: '' }]);
    };

    const handleRemoveTag = (index: number) => {
        onHeroTagsChange(heroTags.filter((_, i) => i !== index));
    };

    const handleUpdateTagLabel = (index: number, label: string) => {
        const updated = [...heroTags];
        updated[index] = { ...updated[index], label };
        onHeroTagsChange(updated);
    };

    const handleUpdateTagDescription = (index: number, description: string) => {
        const updated = [...heroTags];
        updated[index] = { ...updated[index], description };
        onHeroTagsChange(updated);
    };

    return (
        <Card>
            <CardContent className="p-6 space-y-6">
                <div className="flex items-center gap-3 pb-4 border-b border-[#e2e8f0]">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#f59e0b] to-[#fbbf24] flex items-center justify-center flex-shrink-0">
                        <span className="text-white font-bold text-lg">3</span>
                    </div>
                    <div>
                        <h3 className="text-lg font-semibold text-[#1e293b]">Hero Section</h3>
                        <p className="text-sm text-[#64748b]">
                            Add hero images and feature tags for your microsite
                        </p>
                    </div>
                </div>

                {/* Hero Images Section */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between gap-2 bg-[#fef3c7] p-3 rounded-lg border border-[#fde68a]">
                        <div className="flex items-center gap-2">
                            <svg
                                className="w-5 h-5 text-[#f59e0b]"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                            <h4 className="text-md font-semibold text-[#1e293b]">Hero Images</h4>
                        </div>
                        {heroImages.length < 10 && (
                            <button
                                type="button"
                                onClick={handleClick}
                                className="px-4 py-2 bg-[#f59e0b] text-white rounded-lg hover:bg-[#d97706] transition-colors text-sm font-medium"
                            >
                                + Add Images ({heroImages.length}/10)
                            </button>
                        )}
                    </div>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleFileChange}
                        className="hidden"
                    />

                    {/* Images Grid */}
                    {heroImages.length > 0 && (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {heroImages.map((image, index) => (
                                <div
                                    key={index}
                                    className="border border-[#e2e8f0] rounded-lg p-4 space-y-3"
                                >
                                    <div className="relative group">
                                        <img
                                            src={
                                                image.url ||
                                                (image.file ? URL.createObjectURL(image.file) : '')
                                            }
                                            alt={image.altText || `Hero image ${index + 1}`}
                                            className="w-full h-40 object-cover rounded-lg border-2 border-[#e2e8f0]"
                                        />
                                        <div className="absolute top-2 right-2">
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveImage(index)}
                                                className="p-1.5 bg-[#ef4444] text-white rounded-full hover:bg-[#dc2626] transition-colors opacity-0 group-hover:opacity-100"
                                            >
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M6 18L18 6M6 6l12 12"
                                                    />
                                                </svg>
                                            </button>
                                        </div>
                                        <div className="absolute top-2 left-2">
                                            <span className="px-2 py-1 bg-[#1e293b] bg-opacity-70 text-white text-xs rounded font-medium">
                                                {index + 1}
                                            </span>
                                        </div>
                                    </div>

                                    <Input
                                        label="Alt Text"
                                        type="text"
                                        value={image.altText || ''}
                                        onChange={(e) => handleUpdateAltText(index, e.target.value)}
                                        placeholder="Describe this image"
                                        maxLength={100}
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {heroImages.length === 0 && (
                        <button
                            type="button"
                            onClick={handleClick}
                            className="w-full h-32 border-2 border-dashed border-[#cbd5e1] rounded-lg hover:border-[#f59e0b] hover:bg-[#fffbeb] transition-all flex flex-col items-center justify-center gap-2 group"
                        >
                            <svg
                                className="w-10 h-10 text-[#cbd5e1] group-hover:text-[#f59e0b] transition-colors"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 4v16m8-8H4"
                                />
                            </svg>
                            <p className="text-sm text-[#64748b] group-hover:text-[#f59e0b] font-medium">
                                Upload Hero Images
                            </p>
                            <p className="text-xs text-[#94a3b8]">
                                Select multiple files • Max 5MB each • Up to 10 images
                            </p>
                        </button>
                    )}

                    <p className="text-xs text-[#64748b]">
                        Add up to 10 hero images. Each image should have descriptive alt text for
                        SEO and accessibility.
                    </p>
                </div>

                {/* Hero Tags Section */}
                <div className="pt-4 border-t border-[#e2e8f0] space-y-4">
                    <div className="flex items-center gap-2 bg-[#e0e7ff] p-3 rounded-lg border border-[#c7d2fe]">
                        <svg
                            className="w-5 h-5 text-[#6e64f0]"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                            />
                        </svg>
                        <h4 className="text-md font-semibold text-[#1e293b]">Feature Tags</h4>
                    </div>

                    {/* Existing Tags */}
                    {heroTags.length > 0 && (
                        <div className="space-y-3">
                            {heroTags.map((tag, index) => (
                                <div
                                    key={index}
                                    className="border border-[#e2e8f0] rounded-lg p-4 space-y-3"
                                >
                                    <div className="flex items-start gap-3">
                                        <span className="px-2 py-1 bg-[#6e64f0] text-white text-xs rounded font-medium flex-shrink-0 mt-1">
                                            Tag {index + 1}
                                        </span>
                                        <div className="flex-1 space-y-3">
                                            <Input
                                                label="Tag Label"
                                                type="text"
                                                value={tag.label || ''}
                                                onChange={(e) =>
                                                    handleUpdateTagLabel(index, e.target.value)
                                                }
                                                placeholder="Premium Selection"
                                                maxLength={40}
                                            />
                                            <Input
                                                label="Tag Description"
                                                type="text"
                                                value={tag.description || ''}
                                                onChange={(e) =>
                                                    handleUpdateTagDescription(
                                                        index,
                                                        e.target.value
                                                    )
                                                }
                                                placeholder="Curated wines from around the world"
                                                maxLength={100}
                                            />
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => handleRemoveTag(index)}
                                            className="p-2 text-[#ef4444] hover:bg-[#fee2e2] rounded-lg transition-colors flex-shrink-0"
                                        >
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Add Tag Button */}
                    <button
                        type="button"
                        onClick={handleAddTag}
                        className="w-full px-4 py-3 border-2 border-dashed border-[#e2e8f0] rounded-lg text-[#64748b] hover:border-[#6e64f0] hover:text-[#6e64f0] transition-all flex items-center justify-center gap-2"
                    >
                        <svg
                            className="w-5 h-5"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 4v16m8-8H4"
                            />
                        </svg>
                        <span className="font-medium">Add Feature Tag</span>
                    </button>

                    <p className="text-xs text-[#64748b]">
                        Add feature tags to highlight key benefits (e.g., "Premium Selection", "Fast
                        Delivery")
                    </p>
                </div>
            </CardContent>
        </Card>
    );
}
